<template>
  <a-drawer width="78%" :visible="open" :title="formTitle" @close="close">
    <a-form-model ref="form" :model="form" :rules="rules" :label-col="{ span: 5 }" :wrapper-col="{ span: 17 }">
      <a-row :gutter="16">
        <a-col :span="12"><a-form-model-item :label="$t('活动名称')" prop="name"><a-input v-model="form.name" :placeholder="$t('请输入活动名称')" :disabled="readonly" /></a-form-model-item></a-col>
        <a-col :span="6"><a-form-model-item :label="$t('唯一优先级')" prop="priority"><a-input-number v-model="form.priority" :min="1" :precision="0" style="width:100%" :disabled="readonly" /></a-form-model-item></a-col>
        <a-col :span="6"><a-form-model-item :label="$t('门槛口径')" prop="thresholdType"><a-radio-group v-model="form.thresholdType" :disabled="readonly"><a-radio :value="1">{{ $t('原价') }}</a-radio><a-radio :value="2">{{ $t('实付') }}</a-radio></a-radio-group></a-form-model-item></a-col>
      </a-row>
      <a-row :gutter="16">
        <a-col :span="12"><a-form-model-item :label="$t('开始时间')" prop="startTime"><a-date-picker
          v-model="form.startTime"
          show-time
          value-format="YYYY-MM-DD HH:mm:ss"
          format="YYYY-MM-DD HH:mm:ss"
          style="width:100%"
          :disabled="readonly"
        /></a-form-model-item></a-col>
        <a-col :span="12"><a-form-model-item :label="$t('结束时间')" prop="endTime"><a-date-picker
          v-model="form.endTime"
          show-time
          value-format="YYYY-MM-DD HH:mm:ss"
          format="YYYY-MM-DD HH:mm:ss"
          style="width:100%"
          :disabled="readonly"
        /></a-form-model-item></a-col>
      </a-row>
      <a-form-model-item :label="$t('适用范围')" prop="scopeType">
        <a-radio-group v-model="form.scopeType" :disabled="readonly"><a-radio :value="1">{{ $t('全部商品') }}</a-radio><a-radio :value="2">{{ $t('指定商品') }}</a-radio><a-radio :value="3">{{ $t('指定分类') }}</a-radio></a-radio-group>
      </a-form-model-item>
      <a-form-model-item v-if="form.scopeType === 1" :label="$t('排除商品')">
        <a-button :disabled="readonly" @click="chooseGoods('excluded')">{{ $t('选择排除商品') }}</a-button>
        <a-tag v-for="item in excludedGoods" :key="item.id" closable @close="removeGoods('excluded', item.id)">{{ item.goodsName }}</a-tag>
      </a-form-model-item>
      <a-form-model-item v-if="form.scopeType === 2" :label="$t('指定商品')" required>
        <a-button :disabled="readonly" @click="chooseGoods('included')">{{ $t('选择商品') }}</a-button>
        <a-tag v-for="item in includedGoods" :key="item.id" closable @close="removeGoods('included', item.id)">{{ item.goodsName }}</a-tag>
      </a-form-model-item>
      <a-form-model-item v-if="form.scopeType === 3" :label="$t('指定分类')" required>
        <a-tree-select
          v-model="form.categoryIds"
          multiple
          tree-checkable
          :tree-data="categoryOptions"
          :replace-fields="{ children: 'children', title: 'categoryName', key: 'id', value: 'id' }"
          :placeholder="$t('请选择指定分类')"
          style="width:100%"
          :disabled="readonly"
        />
      </a-form-model-item>

      <a-divider orientation="left">{{ $t('满赠阶梯') }}</a-divider>
      <a-alert v-if="tierError" :message="tierError" type="error" show-icon style="margin-bottom:12px" />
      <a-card v-for="(tier, tierIndex) in form.tiers" :key="tier.key" size="small" style="margin-bottom:12px">
        <span slot="title">{{ $t('阶梯') }} {{ tierIndex + 1 }}</span>
        <a-button v-if="!readonly && form.tiers.length > 1" slot="extra" type="link" @click="form.tiers.splice(tierIndex, 1)">{{ $t('删除阶梯') }}</a-button>
        <a-row :gutter="12">
          <a-col :span="8"><a-form-model-item :label="$t('门槛金额')" :label-col="{span:8}" :wrapper-col="{span:16}"><a-input-number v-model="tier.thresholdAmount" :min="0.01" :step="0.01" style="width:100%" :disabled="readonly" /></a-form-model-item></a-col>
          <a-col :span="8"><a-form-model-item :label="$t('阶梯库存')" :label-col="{span:8}" :wrapper-col="{span:16}"><a-input-number v-model="tier.totalStock" :min="1" :precision="0" style="width:100%" :disabled="readonly" /></a-form-model-item></a-col>
          <a-col :span="8"><a-form-model-item :label="$t('每人限领')" :label-col="{span:8}" :wrapper-col="{span:16}"><a-input-number v-model="tier.receiveLimit" :min="1" :precision="0" style="width:100%" :disabled="readonly" /></a-form-model-item></a-col>
        </a-row>
        <a-button v-if="!readonly" type="dashed" @click="chooseSku(tierIndex)">{{ $t('选择赠品 SKU') }}</a-button>
        <a-table row-key="skuId" size="small" :pagination="false" :data-source="tier.skus" :columns="skuColumns">
          <template slot="giftQuantity" slot-scope="text, sku"><a-input-number v-model="sku.giftQuantity" :min="1" :precision="0" :disabled="readonly" /></template>
          <template slot="operation" slot-scope="text, sku"><a v-if="!readonly" @click="tier.skus = tier.skus.filter(item => item.skuId !== sku.skuId)">{{ $t('删除') }}</a></template>
        </a-table>
      </a-card>
      <a-button v-if="!readonly" type="dashed" block @click="addTier"><a-icon type="plus" /> {{ $t('添加阶梯') }}</a-button>
      <div class="bottom-control">
        <a-space><a-button v-if="!readonly" type="primary" :loading="saving" @click="submit">{{ $t('保存') }}</a-button><a-button @click="close">{{ $t('取消') }}</a-button></a-space>
      </div>
    </a-form-model>
    <checkbox-goods-select-form ref="goodsSelect" @goodsSelect="onGoodsSelect" />
    <gift-sku-select-form ref="skuSelect" @select="onSkuSelect" />
  </a-drawer>
</template>

<script>
import { addFullGift, getFullGift, updateFullGift } from '@/api/activity/fullGift'
import { listCategory } from '@/api/goods/category'
import CheckboxGoodsSelectForm from '@/views/goods/goods/modules/CheckboxSelectForm'
import GiftSkuSelectForm from './GiftSkuSelectForm'

const newTier = () => ({ key: `${Date.now()}-${Math.random()}`, thresholdAmount: undefined, totalStock: 1, receiveLimit: 1, skus: [] })
const newForm = () => ({ id: undefined, name: '', startTime: undefined, endTime: undefined, scopeType: 1, thresholdType: 1, priority: 1, goodsIds: [], categoryIds: [], excludedGoodsIds: [], tiers: [newTier()] })

export default {
  name: 'FullGiftCreateForm',
  components: { CheckboxGoodsSelectForm, GiftSkuSelectForm },
  data () {
    return {
      open: false,
      saving: false,
      readonly: false,
      formTitle: this.$t('新增满赠活动'),
      form: newForm(),
      includedGoods: [],
      excludedGoods: [],
      categoryOptions: [],
      goodsMode: 'included',
      currentTier: 0,
      tierError: '',
      rules: {
        name: [{ required: true, message: this.$t('请输入活动名称'), trigger: 'blur' }],
        startTime: [{ required: true, message: this.$t('请选择开始时间'), trigger: 'change' }],
        endTime: [{ required: true, message: this.$t('请选择结束时间'), trigger: 'change' }],
        scopeType: [{ required: true, message: this.$t('请选择适用范围'), trigger: 'change' }],
        thresholdType: [{ required: true, message: this.$t('请选择门槛口径'), trigger: 'change' }],
        priority: [{ required: true, message: this.$t('请输入唯一优先级'), trigger: 'change' }]
      },
      skuColumns: [
        { title: this.$t('商品'), dataIndex: 'goodsName' },
        { title: 'SKU', dataIndex: 'skuName' },
        { title: 'SKU ID', dataIndex: 'skuId' },
        { title: this.$t('赠送数量'), dataIndex: 'giftQuantity', scopedSlots: { customRender: 'giftQuantity' } },
        { title: this.$t('操作'), scopedSlots: { customRender: 'operation' } }
      ]
    }
  },
  created () {
    listCategory().then(response => { this.categoryOptions = this.handleTree(response.data || [], 'id', 'pid') })
  },
  methods: {
    handleAdd () {
      this.form = newForm()
      this.includedGoods = []
      this.excludedGoods = []
      this.readonly = false
      this.formTitle = this.$t('新增满赠活动')
      this.tierError = ''
      this.open = true
    },
    async handleUpdate (row) {
      const response = await getFullGift({ id: row.id })
      const detail = response.data
      const activity = detail.activity
      this.form = Object.assign(newForm(), activity, {
        goodsIds: detail.scopes.filter(item => item.relationType === 1).map(item => item.relationId),
        categoryIds: detail.scopes.filter(item => item.relationType === 2).map(item => item.relationId),
        excludedGoodsIds: detail.scopes.filter(item => item.relationType === 3).map(item => item.relationId),
        tiers: detail.tiers.map(tier => Object.assign({}, tier, { key: tier.id, skus: tier.skus.map(sku => Object.assign({}, sku)) }))
      })
      this.includedGoods = this.form.goodsIds.map(id => ({ id, goodsName: `${this.$t('商品')} ${id}` }))
      this.excludedGoods = this.form.excludedGoodsIds.map(id => ({ id, goodsName: `${this.$t('商品')} ${id}` }))
      this.readonly = row.activityStatus === 2
      this.formTitle = this.$t(this.readonly ? '查看满赠活动（进行中不可编辑）' : '编辑满赠活动')
      this.tierError = ''
      this.open = true
    },
    chooseGoods (mode) {
      this.goodsMode = mode
      const records = mode === 'included' ? this.includedGoods : this.excludedGoods
      this.$refs.goodsSelect.openGoods(undefined, records)
    },
    onGoodsSelect (records) {
      if (this.goodsMode === 'included') this.includedGoods = records
      else this.excludedGoods = records
    },
    removeGoods (mode, id) {
      if (mode === 'included') this.includedGoods = this.includedGoods.filter(item => item.id !== id)
      else this.excludedGoods = this.excludedGoods.filter(item => item.id !== id)
    },
    addTier () { this.form.tiers.push(newTier()) },
    chooseSku (index) {
      this.currentTier = index
      this.$refs.skuSelect.show(this.form.tiers[index].skus)
    },
    onSkuSelect (rows) {
      const old = this.form.tiers[this.currentTier].skus
      this.form.tiers[this.currentTier].skus = rows.map(row => {
        const saved = old.find(item => item.skuId === row.id)
        return { skuId: row.id, goodsName: row.goodsName, skuName: row.skuName || row.skuCode, giftQuantity: saved ? saved.giftQuantity : 1 }
      })
    },
    validateTiers () {
      if (this.form.scopeType === 2 && !this.includedGoods.length) return this.$t('指定商品范围至少选择一个商品')
      if (this.form.scopeType === 3 && !this.form.categoryIds.length) return this.$t('指定分类范围至少选择一个分类')
      let previous = 0
      for (let index = 0; index < this.form.tiers.length; index += 1) {
        const tier = this.form.tiers[index]
        if (!(tier.thresholdAmount > previous)) return this.$t('阶梯门槛必须严格递增且不可重复')
        if (!(tier.totalStock > 0)) return `${this.$t('阶梯')} ${index + 1} ${this.$t('库存必须大于 0')}`
        if (!(tier.receiveLimit > 0)) return `${this.$t('阶梯')} ${index + 1} ${this.$t('限领必须大于 0')}`
        if (!tier.skus.length) return `${this.$t('阶梯')} ${index + 1} ${this.$t('至少选择一个赠品 SKU')}`
        if (tier.skus.some(item => !(item.giftQuantity > 0))) return `${this.$t('阶梯')} ${index + 1} ${this.$t('赠送数量必须大于 0')}`
        previous = Number(tier.thresholdAmount)
      }
      return ''
    },
    submit () {
      this.$refs.form.validate(async valid => {
        if (!valid) return
        this.tierError = this.validateTiers()
        if (this.tierError) return
        if (this.form.startTime >= this.form.endTime) {
          this.tierError = this.$t('开始时间必须早于结束时间')
          return
        }
        const data = Object.assign({}, this.form, {
          goodsIds: this.form.scopeType === 2 ? this.includedGoods.map(item => item.id) : [],
          excludedGoodsIds: this.form.scopeType === 1 ? this.excludedGoods.map(item => item.id) : [],
          categoryIds: this.form.scopeType === 3 ? this.form.categoryIds : [],
          tiers: this.form.tiers.map(tier => ({ id: tier.id, thresholdAmount: tier.thresholdAmount, totalStock: tier.totalStock, receiveLimit: tier.receiveLimit, skus: tier.skus.map(sku => ({ skuId: sku.skuId, giftQuantity: sku.giftQuantity })) }))
        })
        this.saving = true
        try {
          if (data.id) await updateFullGift(data)
          else await addFullGift(data)
          this.$message.success(this.$t('保存成功'))
          this.open = false
          this.$emit('ok')
        } finally {
          this.saving = false
        }
      })
    },
    close () { this.open = false }
  }
}
</script>

<style scoped>
.bottom-control { margin-top: 24px; text-align: center; }
</style>
